import { Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';

import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import styles from './SharedFollowSearchBanner.scss';
import { trackFollowButtonClick } from '../../utils/tracking/searchBrowse/trackFollowSearch';
import { locations } from '../../utils/tracking/shared/followSearchTracking';

import { getFilterValues } from '../../finding/SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import { pageTypeConstants } from '../../constants/pageTypeConstants';

const SharedFollowSearchBannerComponent = props => {
    const {
        viewer,
        itemSearch,
        user,
        uriRef,
        showFollowButton,
        followSearchValue,
        onFollowSearchCompleted,
        isCategorySearch,
        isStaticSbAltStyle,
    } = props;

    if (!showFollowButton) {
        return null;
    }

    const isFollowing = props.isFollowing ?? !!user?.favorites?.edges?.[0]?.node?.serviceId;
    const { followSearchRedesign } = viewer || {};
    const { appliedFilters = [], pageType } = itemSearch || {};

    let displayName = followSearchValue;
    const categoryL1 = getFilterValues(appliedFilters, 'categoryL1')[0]?.displayName;
    const categoryL2 = getFilterValues(appliedFilters, 'categoryL2')[0]?.displayName;
    let messageType = isCategorySearch ? 'noQuotes' : 'quotes';

    if (isCategorySearch && !categoryL2 && pageType === pageTypeConstants.MORE_FROM_SELLER) {
        displayName = getFilterValues(appliedFilters, 'sellerPk')[0].displayName;
        if (categoryL1) {
            messageType = 'withCategory';
        }
    }

    return (
        <div
            data-tn="follow-search-banner"
            className={isStaticSbAltStyle ? styles.alternativeContainer : styles.container}
        >
            {isStaticSbAltStyle && (
                <div className={styles.alternativeCopy}>
                    <FormattedMessage
                        id="sb.FollowSearch.notificationWhenAddedToSearch"
                        defaultMessage="Get notified when items are added to this search"
                    />
                </div>
            )}
            {!isStaticSbAltStyle &&
                (isFollowing ? (
                    <div className={styles.copy}>
                        <FormattedMessage
                            id="sb.FollowSearch.startedFollowingText"
                            defaultMessage='Check your email for weekly updates in <bold>{messageType, select,
                                quotes {"{name}"}
                                withCategory {{name} {categoryL1}}
                                other {{name}}}</bold>.'
                            values={{
                                messageType,
                                bold: chunks => (
                                    <span
                                        data-tn="follow-search-value"
                                        className={styles.searchTerm}
                                    >
                                        {chunks}
                                    </span>
                                ),
                                name: displayName,
                                categoryL1,
                            }}
                        />
                    </div>
                ) : (
                    <Fragment>
                        <div className={styles.title}>
                            <FormattedMessage
                                id="sb.SharedFollowSearchBanner.title"
                                defaultMessage="Get Updated with New Arrivals"
                            />
                        </div>
                        <div className={styles.copy}>
                            <FormattedMessage
                                id="sb.SharedFollowSearchBanner.text"
                                defaultMessage='{followSearchRedesign, select,
                                    true {Save}
                                    other {Follow}
                                } <bold>{messageType, select,
                                    quotes {"{name}"}
                                    withCategory {{name} {categoryL1}}
                                    other {{name}}}</bold>, and we’ll notify you when there are new listings in this category.'
                                values={{
                                    messageType,
                                    bold: chunks => (
                                        <span
                                            data-tn="follow-search-value"
                                            className={styles.searchTerm}
                                        >
                                            {chunks}
                                        </span>
                                    ),
                                    name: displayName,
                                    categoryL1,
                                    followSearchRedesign,
                                }}
                            />
                        </div>
                    </Fragment>
                ))}
            <SaveSearchButton
                className={isStaticSbAltStyle ? styles.alternativeSearchButton : null}
                size="medium"
                userId={get(user, 'serviceId') || null}
                user={user}
                page={uriRef}
                name={followSearchValue}
                connectionKey="SharedFollowSearchBanner_favorites"
                onFollowSearchCompleted={() => {
                    // isFollowing value should now be opposite of what it was
                    trackFollowButtonClick({
                        isFollowing: !isFollowing,
                        label: 'bottom of results',
                    });
                    onFollowSearchCompleted();
                }}
                favoritesConnection={get(props, 'user.favorites') || null}
                location={locations.SEARCH_BROWSE}
                viewer={viewer}
                itemSearch={itemSearch}
            />
        </div>
    );
};

SharedFollowSearchBannerComponent.defaultProps = {
    onFollowSearchCompleted: () => {},
};

SharedFollowSearchBannerComponent.propTypes = {
    showFollowButton: PropTypes.bool.isRequired,
    isFollowing: PropTypes.bool,
    followSearchValue: PropTypes.string,
    onFollowSearchCompleted: PropTypes.func,
    uriRef: PropTypes.string,
    user: PropTypes.object,
    isCategorySearch: PropTypes.bool.isRequired,
    isStaticSbAltStyle: PropTypes.bool,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
};

export const SharedFollowSearchBanner = createFragmentContainer(SharedFollowSearchBannerComponent, {
    viewer: graphql`
        fragment SharedFollowSearchBanner_viewer on Viewer {
            followSearchRedesign: featureFlag(feature: "followSearchRedesign")
            ...SaveSearchButton_viewer
        }
    `,
    user: graphql`
        fragment SharedFollowSearchBanner_user on User {
            serviceId
            favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages)
                @connection(key: "SharedFollowSearchBanner_favorites", filters: []) {
                edges {
                    node {
                        serviceId
                    }
                }
                ...SaveSearchButton_favoritesConnection
            }
        }
    `,
    itemSearch: graphql`
        fragment SharedFollowSearchBanner_itemSearch on ItemSearchQueryConnection {
            pageType
            appliedFilters {
                name
                values {
                    displayName
                }
            }
            ...SaveSearchButton_itemSearch
        }
    `,
});
